import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {LoanTile} from '../tiles/tiles';
import {LoanDetailsService} from '../../../../service/loan-details.service';
import {LoansAndApplicationsSegregator} from '../loans-and-applications-segregator';
import {TileFactory} from '../tiles/tile-factory';
import {Loan} from '../../../../model/loan.model';
import {LoanApplicationService} from '../../../../service/loan-application.service';
import {LoanApplicationTransition} from '../../../../model/LoanApplicationTransition';
import {LoanApplications} from '../../../../model/loan-applications.model';
import {Observable} from 'rxjs';
import {MatTableDataSource} from '@angular/material/table';
import {LoanApplication} from '../../../../model/loan-application.model';
import {ChannelPartner, ChannelPartnerGroupType} from '../../../../model/channel-partner.model';
import {CommonUtil} from '../../../../utils/common.util';
import {ChannelPartnerService} from '../../../../service/channel-partner.service';

@Component({
  selector: 'app-past-applications',
  templateUrl: './past-applications.component.html',
  styleUrls: ['./past-applications.component.scss', '../../../../../assets/styles/loan-tiles.scss']
})
export class PastApplicationsComponent implements OnInit, OnDestroy {
  @Input()
  userId: string;
  loanTiles: LoanTile[];

  loanTilesObservables: Observable<any>;
  dataSource: MatTableDataSource<LoanTile>;
  defaultPageSize = 5;

  constructor(private loanApplicationService: LoanApplicationService, private loanDetailsService: LoanDetailsService, private segregator: LoansAndApplicationsSegregator,
              private channelPartnerService: ChannelPartnerService, private tileFactory: TileFactory) {
    this.dataSource =  new MatTableDataSource<LoanTile>();
  }

  async ngOnInit(): Promise<void> {
    const [loanApplications, loans] = await Promise.all([
      this.loanApplicationService.getLoanApplicationsByUserId(this.userId),
      this.loanDetailsService.getLoansByUserId(this.userId)
    ]);
    const loanApplicationIds = loanApplications.map(loanApplication => loanApplication.id);
    const transitions: LoanApplicationTransition[] = await this.loanApplicationService.getAllTransitionsByLoanApplicationIds(loanApplicationIds);
    const oldLoanApplications = this.segregator.filterOldRejectedLoanApplications(loanApplications, loans, transitions);

    if (oldLoanApplications) {
      this.loanTiles = [];

      const partners = await this.getPartners(loanApplications);
      const partnersWithPartnerGroupsMapping = await this.setIfPartnerBelongsToSubventedPreApprovalPartnerGroup(partners);

      this.loanTiles.push(...this.constructTilesForLoans(oldLoanApplications, loans, transitions, partnersWithPartnerGroupsMapping)
          .sort((firstLoanTile, secondLoanTile) =>
          new Date(secondLoanTile.applicationDate).getTime() - new Date(firstLoanTile.applicationDate).getTime()));
    } else {
      this.loanTiles = null;
    }
    this.dataSource.data = this.loanTiles;
    this.loanTilesObservables = this.dataSource.connect();
  }

  ngOnDestroy() {
    if (this.dataSource) {
      this.dataSource.disconnect();
    }
  }

  constructTilesForLoans(loanApplications: LoanApplications, loans: Loan[], transitions: LoanApplicationTransition[],
                         partnersWithPartnerGroupsMapping: Map<string, boolean>): LoanTile[] | null {

    return loanApplications.length > 0 ? loanApplications.map(loanApplication => {
      const loan = this.findLoanByLoanApplicationId(loanApplication.id, loans);
      if (partnersWithPartnerGroupsMapping.get(loanApplication.partnerId)) {
        loanApplication.tenure = null;
        if (loan) {
          loan.loan_tenure = null;
        }
      }

      const transitionsForLoanApplication = transitions.filter(transition => transition.entityId === loanApplication.id);
      return this.tileFactory.createForRejectedOrClosedApplication(loanApplication, loan, transitionsForLoanApplication);
    }) : [];
  }

  private async getPartners(loanApplications: LoanApplication[]): Promise<ChannelPartner[]> {
    const partnerIds = Array.from(new Set<string>(loanApplications.filter(loanApplication => !CommonUtil.isEmpty(loanApplication.partnerId))
        .map(loanApplication => loanApplication.partnerId)));
    return partnerIds && partnerIds.length > 0 ? await this.channelPartnerService.getPartnerByIds(partnerIds) : [];
  }

  private async setIfPartnerBelongsToSubventedPreApprovalPartnerGroup(partners: ChannelPartner[]): Promise<Map<string, boolean>> {
    const map = new Map<string, boolean>();
    if (partners.length > 0) {
      const partnerGroup = await this.channelPartnerService.getChannelPartnerGroupByType(ChannelPartnerGroupType.SubventedPreApproval);
      partners.forEach(partner => {
        if (partner.partnerGroupIds && partner.partnerGroupIds.includes(partnerGroup._id)) {
          map.set(partner.id, true);
        } else {
          map.set(partner.id, false);
        }
      });
    }
    return map;
  }

  private findLoanByLoanApplicationId(loanApplicationId: string, loans: Loan[]) {
    return loans.find(loanForApplication => loanForApplication.loanApplicationId === loanApplicationId);
  }
}
